export const FAMILY_CONTRIBUTOR_PHOTO_LIMIT = 5 as const;

export interface ProfileOwnershipAuthorityInput {
  profileState: 'placeholder' | 'claimed';
  claimedByUserId: string | null;
  currentUserId: string | null;
  createdByUserId: string | null;
  graphOwnerUserId: string | null;
  isGraphStaff: boolean;
  hasPaidEntitlement: boolean;
  existingPhotoCount: number;
}

export interface ProfileOwnershipAuthorityReport {
  isClaimed: boolean;
  isClaimOwner: boolean;
  isUnclaimedMaintainer: boolean;

  canEditPersonalInfo: boolean;
  canUploadPhotos: boolean;
  canUploadVideos: boolean;
  canDeleteMedia: boolean;
  canContributeProfileMemories: boolean;

  photoLimit: number | null;
  existingPhotoCount: number;
  remainingPhotoCount: number | null;

  reason:
    | 'CLAIM_OWNER_PAID'
    | 'CLAIM_OWNER_FAMILY_CONTRIBUTOR'
    | 'UNCLAIMED_PROFILE_MAINTAINER'
    | 'CLAIMED_PROFILE_LOCKED'
    | 'UNAUTHORIZED';
}

function normalizeCount(value: number): number {
  return Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

export function projectProfileOwnershipAuthority(
  input: ProfileOwnershipAuthorityInput,
): ProfileOwnershipAuthorityReport {
  const existingPhotoCount =
    normalizeCount(input.existingPhotoCount);

  const isClaimed =
    input.profileState === 'claimed' ||
    Boolean(input.claimedByUserId);

  const isClaimOwner =
    Boolean(input.currentUserId) &&
    input.claimedByUserId === input.currentUserId;

  const isOriginalCreator =
    Boolean(input.currentUserId) &&
    input.createdByUserId === input.currentUserId;

  const isGraphOwner =
    input.isGraphStaff &&
    Boolean(input.currentUserId) &&
    input.graphOwnerUserId === input.currentUserId;

  const isUnclaimedMaintainer =
    !isClaimed &&
    (isOriginalCreator || isGraphOwner);

  if (isClaimOwner && input.hasPaidEntitlement) {
    return Object.freeze({
      isClaimed,
      isClaimOwner,
      isUnclaimedMaintainer: false,
      canEditPersonalInfo: true,
      canUploadPhotos: true,
      canUploadVideos: true,
      canDeleteMedia: true,
      canContributeProfileMemories: true,
      photoLimit: null,
      existingPhotoCount,
      remainingPhotoCount: null,
      reason: 'CLAIM_OWNER_PAID',
    });
  }

  if (isClaimOwner) {
    const remainingPhotoCount = Math.max(
      0,
      FAMILY_CONTRIBUTOR_PHOTO_LIMIT -
        existingPhotoCount,
    );

    return Object.freeze({
      isClaimed,
      isClaimOwner,
      isUnclaimedMaintainer: false,
      canEditPersonalInfo: true,
      canUploadPhotos: remainingPhotoCount > 0,
      canUploadVideos: false,
      canDeleteMedia: true,
      canContributeProfileMemories: true,
      photoLimit: FAMILY_CONTRIBUTOR_PHOTO_LIMIT,
      existingPhotoCount,
      remainingPhotoCount,
      reason: 'CLAIM_OWNER_FAMILY_CONTRIBUTOR',
    });
  }

  if (isUnclaimedMaintainer) {
    return Object.freeze({
      isClaimed: false,
      isClaimOwner: false,
      isUnclaimedMaintainer: true,
      canEditPersonalInfo: true,
      canUploadPhotos: true,
      canUploadVideos: true,
      canDeleteMedia: true,
      canContributeProfileMemories: true,
      photoLimit: null,
      existingPhotoCount,
      remainingPhotoCount: null,
      reason: 'UNCLAIMED_PROFILE_MAINTAINER',
    });
  }

  return Object.freeze({
    isClaimed,
    isClaimOwner: false,
    isUnclaimedMaintainer: false,
    canEditPersonalInfo: false,
    canUploadPhotos: false,
    canUploadVideos: false,
    canDeleteMedia: false,
    canContributeProfileMemories: false,
    photoLimit:
      isClaimed && !input.hasPaidEntitlement
        ? FAMILY_CONTRIBUTOR_PHOTO_LIMIT
        : null,
    existingPhotoCount,
    remainingPhotoCount:
      isClaimed && !input.hasPaidEntitlement
        ? Math.max(
            0,
            FAMILY_CONTRIBUTOR_PHOTO_LIMIT -
              existingPhotoCount,
          )
        : null,
    reason: isClaimed
      ? 'CLAIMED_PROFILE_LOCKED'
      : 'UNAUTHORIZED',
  });
}

export const PROFILE_OWNERSHIP_AUTHORITY_READY = true;
